import { useState, useCallback, useEffect } from 'react';
import { DeviceType } from './Device';

// for device types and items, also see deviceChartViewDataList memo in InsightsAndCharts.js
function getIntresetedStateChartItems(type, limitItems=false) {
    let itemsToAsk = undefined;
    switch (type) {
        case DeviceType.plug:
        case DeviceType.lamp:
        case DeviceType.lampX1TwoWay:
            itemsToAsk = ["state"];
            break;
        case DeviceType.dpst:
            itemsToAsk = ["state1", "state2"];
            break;
        case DeviceType.lampX3:
            itemsToAsk = ["state1", "state2", "state3"];
            break;
        case DeviceType.lampX4:
            itemsToAsk = ["state1", "state2", "state3", "state4"];
            break;
        case DeviceType.curtain1Lamp2:
            itemsToAsk = ["lamp1", "lamp2", "curtain"];
            break;
        case DeviceType.relay4:
            itemsToAsk = ["channel1", "channel2", "channel3", "channel4"];
            break;
        case DeviceType.evaporativeCooler:
            itemsToAsk = ["power", "pump", "slow_fan", "fast_fan"];
            break;
        case DeviceType.wc:
            itemsToAsk = ["lamp", "fan", "spray", "music"];
            break;
        case DeviceType.curtain:
            itemsToAsk = ["channel1", "channel2"];
            break;
        case DeviceType.doorSensor:
        case DeviceType.motion:
        case DeviceType.wifiDoorSensor:
        case DeviceType.wifiMotionSensor:
            itemsToAsk = ["state"];
            break;
        case DeviceType.multipurposeSensor:
            if (limitItems) {
                itemsToAsk = ["motion", "sound", "temperature"];
            } else {
                itemsToAsk = ["motion", "sound", "temperature", "humidity", "light_level"];
            }
            break;
        case DeviceType.hinavaEcoThermostat:
            itemsToAsk = ["env_temperature", "set_point"];
            break;
        default:
            break;
    }
    return itemsToAsk
}
const defaultDeviceTypesForStateChart = [
    DeviceType.doorSensor,
    DeviceType.motion,
    DeviceType.wifiDoorSensor,
    DeviceType.wifiMotionSensor,
    DeviceType.multipurposeSensor,
    DeviceType.hinavaEcoThermostat,
];

export default function useInsightsAndCharts(devices, sendEvent, negotiatedProtocol) {
    const [insights, setInsights] = useState({
        automation_trigger: undefined, // count, duration
        scenario_run: undefined, // count, duration
        device_change_sources: undefined, // scenario, automation, manual, application, duration
        active_users: undefined, // users: [{user_id, visits, commands}], duration
    });
    const [allLampsChart, setAllLampsChart] = useState(undefined);

    const [lastStateChartRefreshTime, setLastStateChartRefreshTime] = useState(0);
    const [stateChartPerMac, setStateChartPerMac] = useState({});
    // stateChartPerMac: { mac -> { state_name1: values1, ..., currentTimestamp }}

    const [requestedMacs, setRequestedMacs] = useState([]);
    const requestStateChartForDevice = useCallback(({ mac, type }) => {        
        if (!requestedMacs.includes(mac)) {
            let itemsToAsk = getIntresetedStateChartItems(type, false);
            if (itemsToAsk !== undefined) {
                setRequestedMacs((lastList) => {
                    if (!lastList.includes(mac)) {
                        const newList = [...lastList];
                        newList.push(mac);
                        return newList;
                    } else {
                        return lastList;
                    }
                });
                sendEvent("state_chart", { devices: [{ mac: mac, items: itemsToAsk }] });
            }
        }
    }, [sendEvent, requestedMacs]);

    const getDefaultCharts = useCallback(() => {
        const messageToSend = [];
        for (let i = 0; i < devices.length; i++) {
            const device = devices[i];
            if (defaultDeviceTypesForStateChart.includes(device.type)) {
                let itemsToAsk = getIntresetedStateChartItems(device.type, true);
                if (itemsToAsk !== undefined) {
                    messageToSend.push({ mac: device.mac, items: itemsToAsk });
                }
            }
        }
        if (messageToSend.length > 0) {
            // TODO: اینو کامنت کردم بخاطر سنسور چند منظوره و این که توی صفحه اصلی سطح نور و رطوبت رو نمیخوایم
            // اگه کامنت نشه، دیگه اون‌ها گرفته نمیشن توی صفحه جزییات هم
            // اگه اون‌ها همیشه گرفته بشن، الکی یه پردازشی میوفته روی سرور و هاب
            // setRequestedMacs((lastList) => {
            //     const newList = [...lastList]
            //     for (let i = 0; i < messageToSend.length; i++) {
            //         const mac = messageToSend[i].mac;
            //         if (!newList.includes(mac)) {
            //             newList.push(mac);
            //         }
            //     }
            //     return newList;
            // });
            sendEvent("state_chart", { devices: messageToSend });
        }
    }, [devices, sendEvent]);


    useEffect(() => {
        const currentTimestamp = (new Date()).getTime();
        if (
            (negotiatedProtocol.supportInsightsAndCharts) &&
            (currentTimestamp - lastStateChartRefreshTime > 1000 * 60 * 60 * 1) && // 1 hours
            (devices.length > 0)) { // loaded device list
            getDefaultCharts();
            setLastStateChartRefreshTime(currentTimestamp);
        }
    }, [negotiatedProtocol.supportInsightsAndCharts, getDefaultCharts, lastStateChartRefreshTime, devices]); // will trigger on change on devices

    const onInsightsOrChartsEvent = useCallback((message) => {
        if (message.ev === "insights") {
            setInsights(message.msg);
        } else if (message.ev === "all_lamps_chart") {
            setAllLampsChart(message.msg);
        } else if (message.ev === "state_chart") {
            // const currentTimestamp = Math.round((new Date()).getTime() / 1000);
            setStateChartPerMac((sc) => {
                let sc2 = { ...sc };
                for (let i = 0; i < message.msg.devices.length; i++) {
                    const d = message.msg.devices[i];
                    sc2[d.mac] = { ...(d.data) };
                }
                return sc2;
            });
        }
    }, []);

    return { insights, allLampsChart, stateChartPerMac, 
        onInsightsOrChartsEvent, setLastStateChartRefreshTime, 
        requestStateChartForDevice };
}
